import * as React from "react";
import type { CurrencyCode } from "@forgepoint/shared";
import { cn, formatCurrency, formatUnitPrice } from "@/lib/utils";
import { GHOST_FIELD_CLASSES } from "./GhostInput";

interface CurrencyCellProps {
  value: string | null | undefined;
  onChange?: (value: string) => void;
  disabled?: boolean;
  className?: string;
  editable?: boolean;
  /** When true, displays up to 3 decimal places for per-unit values. Defaults to false (2 decimals). */
  isUnitPrice?: boolean;
  /** Currency the value is formatted in. Defaults to USD. */
  currencyCode?: CurrencyCode;
  /**
   * Unit-price display fraction digits (the org's `quotePriceRounding.decimals`);
   * only applied when `isUnitPrice` is true. Amounts always render at the
   * currency's ISO minor units.
   */
  decimals?: number;
}

export function CurrencyCell({
  value,
  onChange,
  disabled = false,
  className,
  editable = true,
  isUnitPrice = false,
  currencyCode,
  decimals,
}: CurrencyCellProps) {
  const [isFocused, setIsFocused] = React.useState(false);
  const [localValue, setLocalValue] = React.useState(value ?? "");
  const format = (amount: string) =>
    isUnitPrice
      ? formatUnitPrice(amount, currencyCode, decimals)
      : formatCurrency(amount, currencyCode);
  const step = isUnitPrice ? "0.001" : "0.01";

  React.useEffect(() => {
    if (!isFocused) {
      setLocalValue(value ?? "");
    }
  }, [value, isFocused]);

  if (!editable || !onChange) {
    return (
      <span className={cn("text-xs sm:text-sm tabular-nums", className)}>
        {value ? format(value) : "-"}
      </span>
    );
  }

  const displayValue = isFocused ? localValue : value ? format(value) : "";

  const handleFocus = () => {
    setIsFocused(true);
    setLocalValue(value ?? "");
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (localValue !== value) {
      onChange(localValue);
    }
  };

  return (
    <input
      type={isFocused ? "number" : "text"}
      step={step}
      value={displayValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder="-"
      className={cn(
        "flex h-6 sm:h-8 w-full rounded-md px-1 sm:px-2 py-0.5 sm:py-1 text-xs sm:text-sm text-right tabular-nums transition-colors",
        isUnitPrice ? "min-w-28 sm:min-w-32" : "min-w-0",
        "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
        GHOST_FIELD_CLASSES,
        "placeholder:text-muted-foreground",
        className,
      )}
    />
  );
}
